import { formLabels } from '../types';
import type { DashboardRow } from '../dashboardTypes';
import { DASH, formatDay, formatInstant, formatMoney } from '../format';
import { StatusBadge } from './StatusBadge';

/** A value the server has no source for yet: a visible dash with a screen-reader note, never an invented value. */
function Unavailable({ label }: { label: string }) {
  return <span title={`${label}: not available yet`}>{DASH}<span className="tpd-visually-hidden"> {label} not available yet</span></span>;
}

/**
 * The full table (SRS 10). Horizontal overflow stays INSIDE this container (the page itself never scrolls sideways), the header stays visible, and the scroll area is
 * focusable so a keyboard user can scroll it. The Company column (with the masked EIN) is for the Super Admin view only. There is no Transaction ID column: no
 * confirmation reference exists on the server yet (BACKEND GAP).
 */
export function PaymentsTable({ rows, showCompany, companyName, selectedId, onOpen }: {
  rows: DashboardRow[];
  showCompany: boolean;
  companyName: (companyId: string) => string | null;
  selectedId: string | null;
  onOpen: (row: DashboardRow, opener: HTMLElement) => void;
}) {
  return (
    <div className="tpd-table-wrap" tabIndex={0} role="region" aria-label="Tax payments table, scrolls sideways">
      <table className="tpd-table" aria-label="Tax payments">
        <thead>
          <tr>
            {showCompany && <th scope="col">Company Name</th>}
            <th scope="col">Period</th>
            <th scope="col">Frequency</th>
            <th scope="col">Tax Type / Form</th>
            <th scope="col">Agency / Jurisdiction</th>
            <th scope="col">Due Date</th>
            <th scope="col">Process No Later Than</th>
            <th scope="col" className="tpd-num">Amount</th>
            <th scope="col">Scheduled Debit / Submission</th>
            <th scope="col">Date Paid</th>
            <th scope="col">Status</th>
            <th scope="col">Payroll Processor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const form = formLabels[row.formCode] ?? row.formCode;
            const label = `View details for ${form} due ${formatDay(row.dueDate)}`;
            const company = companyName(row.companyId);
            const open = (target: HTMLElement) => onOpen(row, target);
            const openButton = (text: string) => (
              <button type="button" className="tpd-rowlink" aria-label={showCompany ? `${label}, ${company ?? 'company'}` : label} onClick={(e) => { e.stopPropagation(); open(e.currentTarget); }}>
                {text}
              </button>
            );
            return (
              <tr key={row.id} className={row.id === selectedId ? 'is-selected' : ''} data-status={row.status} onClick={(e) => open(e.currentTarget.querySelector<HTMLElement>('.tpd-rowlink') ?? e.currentTarget)}>
                {showCompany && (
                  <th scope="row">
                    {openButton(company ?? 'Company')}
                    <span className="tpd-sub">EIN <Unavailable label="EIN" /></span>
                  </th>
                )}
                {showCompany ? <td>{formatDay(row.periodEndDate)}</td> : <th scope="row">{openButton(formatDay(row.periodEndDate))}</th>}
                <td><span className="tpd-chip">{row.frequency}</span></td>
                <td>{form}</td>
                <td>{row.agency}<span className="tpd-sub">{row.jurisdiction}</span></td>
                <td>
                  {formatDay(row.dueDate)}
                  {row.status === 'Overdue' && <span className="tpd-sub tpd-sub--danger">Legal due date passed</span>}
                </td>
                <td><Unavailable label="Process No Later Than" /></td>
                <td className="tpd-num">{formatMoney(row.amount)}</td>
                <td>{row.initiatedAtUtc ? formatInstant(row.initiatedAtUtc) : DASH}</td>
                <td>{formatDay(row.datePaid)}</td>
                <td><StatusBadge status={row.status} /></td>
                <td><Unavailable label="Payroll Processor" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
